import { Sparkles, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  AI_DISCLAIMER_TEXT,
  AI_KIND_LABEL,
  formatAiConfidence,
  type AiAdvisoryKind,
} from "@/lib/aiAdvisory";

interface AIAdvisoryBadgeProps {
  kind: AiAdvisoryKind;
  /** Confidence in [0, 1] when available. */
  confidence?: number | null;
  /** When true, show full label + confidence. When false, compact pill. */
  expanded?: boolean;
  className?: string;
}

/**
 * Single source of truth for "this came from AI" UI. Always says Advisory.
 * Always shows a tooltip with the disclaimer. Optionally renders the
 * confidence so owners can weigh it.
 */
export function AIAdvisoryBadge({
  kind,
  confidence,
  expanded = false,
  className,
}: AIAdvisoryBadgeProps) {
  const conf = formatAiConfidence(confidence);
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
        expanded ? "px-2 py-0.5 text-xs" : "px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide",
        className
      )}
      title={AI_DISCLAIMER_TEXT}
    >
      <Sparkles className="h-3 w-3 shrink-0" aria-hidden />
      {expanded ? (
        <span>
          {AI_KIND_LABEL[kind]} · Advisory
          {confidence != null ? <> · {conf}</> : null}
        </span>
      ) : (
        <span>Advisory{confidence != null ? ` · ${conf}` : ""}</span>
      )}
    </Badge>
  );
}

/** Standardized disclaimer block used near AI-driven content. */
export function AIAdvisoryNotice({ className }: { className?: string }) {
  return (
    <p
      className={cn(
        "flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground",
        className
      )}
    >
      <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
      <span>{AI_DISCLAIMER_TEXT}</span>
    </p>
  );
}
